import { SmartCardProvider } from '@atlaskit/link-provider';
import { EngagementProvider } from '@atlassiansox/engagekit-ts';
import { analyticsClient } from '@databases/analytics';
import { ContentStatusObserver } from '@databases/confluence-shell';
import {
  getCloudId,
  getConfluenceWorkspaceId,
  initializeContentSyncMachine,
  useDatabaseContent,
} from '@databases/database-state';
import { ConfirmDialog, DialogProvider } from '@databases/dialog';
import { DialogsType } from '@databases/dialog/src/context/DialogContext';
import { IntlProviderWithMessages, useLocale } from '@databases/intl';
import { PanelProvider, PanelsType } from '@databases/panel/PanelContext';
import { MenuPortal } from '@databases/react/menuPortal';
import { appStore } from '@databases/redux/store';
import UnsupportedVersionCheck from '@databases/unsupported-version/UnsupportedVersionCheck';
import { getBridgeAPI } from '@databases/utils/database/bridge';
import { isStandalone } from '@databases/utils/utils';
import { ViewProvider } from '@databases/views/ViewContext';
import ViewSettings from '@databases/views/ViewSettings';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Provider } from 'react-redux';

import { dynamicImport } from '@canvas/dynamic-import';

import ErrorBoundary from './common/components/ErrorBoundary';
import AppStyle from './common/components/GlobalAppStyles';
import Scope from './common/components/Scope';
import FilterTypePicker from './common/components/database-content/view-settings-picker/filter-picker/FilterTypePicker';
import FilterValueEdit from './common/components/database-content/view-settings-picker/filter-picker/FilterValueEdit';
import FieldPicker from './common/components/database-field/FieldPicker';
import FieldName from './common/components/database-field/field-name/FieldName';
import { InternalDatabaseValueView } from './common/components/database-value/DatabaseValueView';

interface AppProps extends React.PropsWithChildren {
  scope?: string;
  aaid?: string;
  isValueEmbed?: boolean;
}
interface MaybeScopeProps extends React.PropsWithChildren {
  scope: string | undefined;
}

const MaybeScope: React.FC<MaybeScopeProps> = ({ scope, children }) =>
  scope ? <Scope scope={scope}>{children}</Scope> : <>{children}</>;

const ContentStatusObserverWithId = () => {
  const { contentId } = useDatabaseContent();
  const [updateContentStatus, setUpdateContentStatus] = useState<
    ((data: { status: string; tooltipText: string | undefined }) => void) | undefined
  >(undefined);
  useEffect(() => {
    const loadUpdateContentStatus = async () => {
      setUpdateContentStatus((await getBridgeAPI()).updateBoardStatus);
    };
    if (isStandalone()) {
      loadUpdateContentStatus();
    }
  }, []);

  return contentId ? (
    <ContentStatusObserver
      contentId={contentId}
      updateContentStatus={updateContentStatus}
    />
  ) : null;
};

const DatabaseSnackbar = lazy(() =>
  dynamicImport(() => import('@databases/snackbar/DatabaseSnackbar'))
);
const DevtoolsDialog = lazy(() => dynamicImport(() => import('@databases/devtools')));
const ExportDialog = lazy(() =>
  dynamicImport(() => import('@databases/export/ExportDialog'))
);
const ImportDialog = lazy(() =>
  dynamicImport(() => import('@databases/import/ImportDialog'))
);

const DatabaseSetupPanel = lazy(() =>
  dynamicImport(() => import('@databases/database-setup'))
);
const CopyDatabaseDialog = lazy(() =>
  dynamicImport(() => import('./apps/copy-database-dialog/components/CopyDatabaseDialog'))
);
const FieldSettingsDialog = lazy(() =>
  dynamicImport(
    () => import('./apps/field-settings-dialog/components/FieldSettingsDialog')
  )
);
const DatabaseEntryPanel = lazy(() =>
  dynamicImport(
    () => import('./apps/database-entry-dialog/components/DatabaseEntryPanel')
  )
);
const CreateFieldPanel = lazy(() =>
  dynamicImport(() => import('./apps/create-field-dialog/components/CreateFieldPanel'))
);

const DefaultDialogs = {
  DevtoolsDialog: (
    <Suspense>
      <DevtoolsDialog />
    </Suspense>
  ),
  ConfirmDialog: <ConfirmDialog />,
  ExportDialog: (
    <Suspense>
      <ExportDialog />
    </Suspense>
  ),
  ImportDialog: (
    <Suspense>
      <ImportDialog
        FieldNameComponent={FieldName}
        InternalDatabaseValueViewComponent={InternalDatabaseValueView}
      />
    </Suspense>
  ),
} as DialogsType;

const DefaultPanels = {
  DatabaseSetupPanel: (
    <Suspense>
      <DatabaseSetupPanel />
    </Suspense>
  ),
  FieldSettingsPanel: (
    <Suspense>
      <FieldSettingsDialog />
    </Suspense>
  ),
  FieldCreatePanel: (
    <Suspense>
      <CreateFieldPanel />
    </Suspense>
  ),
  CopyDatabasePanel: (
    <Suspense>
      <CopyDatabaseDialog />
    </Suspense>
  ),
  ViewSettingsPanel: (
    <ViewSettings
      FieldPicker={FieldPicker}
      FilterTypePicker={FilterTypePicker}
      FilterValueEdit={FilterValueEdit}
    />
  ),
  EntryViewPanel: (
    <Suspense>
      <DatabaseEntryPanel />
    </Suspense>
  ),
} as PanelsType;

const App: React.FC<AppProps> = ({
  aaid = '',
  scope,
  children,
  isValueEmbed = false,
}) => {
  initializeContentSyncMachine();

  const locale = useLocale();

  const engageKitConfig = {
    cloudId: getCloudId() ?? '',
    aaid,
    locale,
    product: 'confluence',
    stargateUrl: '/gateway/api',
    workspaceId: getConfluenceWorkspaceId(),
  };

  return (
    <MenuPortal>
      <IntlProviderWithMessages locale={locale}>
        <ErrorBoundary>
          <EngagementProvider
            config={engageKitConfig}
            analyticsClientInstance={analyticsClient.client}
          >
            <SmartCardProvider>
              <MaybeScope scope={scope}>
                <Provider store={appStore}>
                  <ViewProvider>
                    <ContentStatusObserverWithId />
                    <AppStyle isValueEmbed={isValueEmbed} />
                    <UnsupportedVersionCheck />
                    <Suspense>
                      <DatabaseSnackbar />
                    </Suspense>
                    <DialogProvider defaultDialogs={DefaultDialogs}>
                      <PanelProvider defaultPanels={DefaultPanels}>
                        {children}
                      </PanelProvider>
                    </DialogProvider>
                  </ViewProvider>
                </Provider>
              </MaybeScope>
            </SmartCardProvider>
          </EngagementProvider>
        </ErrorBoundary>
      </IntlProviderWithMessages>
    </MenuPortal>
  );
};

export default App;
